import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  totalExtras: number;
  affectedTeams: number;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ResetDuplicatesModal({
  totalExtras,
  affectedTeams,
  loading,
  onConfirm,
  onClose,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useBodyScrollLock(true);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [loading, onClose]);

  const xPart =
    totalExtras === 1 ? '1 figurinha repetida' : `${totalExtras} figurinhas repetidas`;
  const yPart = affectedTeams === 1 ? '1 seleção' : `${affectedTeams} seleções`;
  const verbForm = totalExtras === 1 ? 'trocada' : 'trocadas';
  const body = `Você vai marcar como ${verbForm} ${xPart} de ${yPart}. Cada figurinha fica com 1 unidade (a que você guarda). Essa ação pode ser desfeita por alguns segundos.`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-dupes-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-lg animate-fade-up"
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-ink-100">
          <h2
            id="reset-dupes-title"
            className="flex-1 font-display font-semibold text-h3 text-ink-900"
          >
            Marcar repetidas como trocadas
          </h2>
          <button
            type="button"
            onClick={() => {
              if (!loading) onClose();
            }}
            aria-label="Fechar"
            disabled={loading}
            className="p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100 disabled:opacity-40 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="font-body text-body text-ink-700 leading-relaxed">{body}</p>
        </div>

        <div className="px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] flex flex-col-reverse sm:flex-row gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 h-11 px-5 text-[15px] inline-flex items-center justify-center rounded-lg border-[1.5px] border-transparent font-semibold tracking-tight bg-transparent text-ink-700 hover:bg-ink-100 disabled:opacity-50 disabled:cursor-not-allowed btn-focus transition-colors"
          >
            Cancelar
          </button>
          <Button variant="primary" size="md" fullWidth onClick={onConfirm} loading={loading}>
            Marcar como trocadas
          </Button>
        </div>
      </div>
    </div>
  );
}
